<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { router } from '../lib/router.svelte.ts';
  import CorrectionSheet from '../lib/CorrectionSheet.svelte';
  import { canReopen, canRetract, REOPEN_WINDOW_HOURS } from '@chariot/contract';

  /**
   * What actually happened during one sweep.
   *
   * The reports list says a round found 47 of 50; it cannot say which carts, in what order,
   * or what became of the three that were missing. That question comes up every time a
   * missing cart is chased, so the whole round is readable here — including, for each cart
   * declared missing, whether it has turned up since.
   */

  let { id }: { id: string } = $props();

  /** Set to the cart being recorded as found; null when the sheet is closed. */
  let findingCart = $state<string | null>(null);
  let dropping = $state<{ id: string; at: string; round_id: string; cart_code: string; site_code: string } | null>(null);
  let notice = $state<string | null>(null);

  /**
   * Amending is deliberately short-lived: past the window a re-closure would be diffed
   * against a catalogue that has moved on. `now` is read once per render, which is enough
   * for a screen an operator opens, acts on and leaves.
   */
  async function reopen() {
    if (!round) return;
    const out = await store.reopenRound(round.round_id);
    if (out === 'ok') return router.go('');
    notice =
      out === 'busy'
        ? 'Une tournée est déjà en cours sur cet appareil — terminez-la d\'abord.'
        : out === 'too_old'
          ? `Passé ${REOPEN_WINDOW_HOURS} h, une tournée ne se rouvre plus. Enregistrez le chariot seul.`
          : 'Tournée introuvable sur cet appareil.';
  }

  async function dropScan() {
    if (!dropping) return;
    const out = await store.retractScan(dropping);
    if (out === 'too_old') notice = `Passé ${REOPEN_WINDOW_HOURS} h, un scan ne se retire plus.`;
    dropping = null;
  }

  const round = $derived(store.roundsById.get(id) ?? null);
  const progress = $derived(round ? store.progressForRound(round) : null);
  const scans = $derived(store.scansInRound(id));
  const arrivedFrom = $derived(progress?.arrivedFrom ?? {});
  const expectedHere = $derived(new Set(progress?.expected ?? []));
  const reopenable = $derived(round ? canReopen(round) : false);
  const retracted = $derived(new Set(round?.retracted_scan_ids ?? []));

  /** The conclusion the round actually recorded, not one recomputed today. */
  const missing = $derived(
    round?.status === 'closed' ? round.missing : (progress?.missing ?? []),
  );

  const endedAt = $derived(round?.closed_at ?? round?.abandoned_at ?? null);
  const duration = $derived.by(() => {
    if (!round || !endedAt) return null;
    const min = Math.round((Date.parse(endedAt) - Date.parse(round.opened_at)) / 60_000);
    if (min < 1) return "moins d'une minute";
    if (min < 60) return `${min} min`;
    return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
  });

  const label = (code: string) => store.cartByCode.get(code)?.name ?? code;
  const hms = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR');

  /** A cart declared missing then scanned afterwards has simply been found again. */
  function seenSince(code: string): string | null {
    if (!endedAt) return null;
    const last = store.states.get(code)?.last_seen_at;
    return last && last > endedAt ? last : null;
  }
</script>

<button class="ghost slim no-print" style="margin-left:-.7rem" onclick={() => router.go('reports')}>
  ← Rapports
</button>

{#if !round}
  <p class="banner warn">
    Tournée inconnue sur cet appareil. Synchronisez pour récupérer les tournées des autres
    appareils.
  </p>
{:else}
  <div class="row" style="margin-top:.4rem;margin-bottom:.1rem">
    <h1 class="grow truncate">{store.siteName(round.site_code)}</h1>
    {#if round.mode === 'correction'}
      <span class="pill">hors tournée</span>
    {:else if round.status === 'closed'}
      <span class="pill {missing.length ? 'warn' : 'ok'}">terminée</span>
    {:else if round.status === 'abandoned'}
      <span class="pill">interrompue</span>
    {:else}
      <span class="pill accent">ouverte</span>
    {/if}
  </div>
  <p class="small subtle" style="margin-bottom:1rem">
    {new Date(round.opened_at).toLocaleString('fr-FR')}
    {#if duration} · {duration}{/if}
    {#if round.user_id} · {round.user_id}{/if}
  </p>

  {#if notice}<div class="banner warn">{notice}</div>{/if}

  {#if round.reopen_count > 0}
    <!-- An amended conclusion that looks untouched is worse than no amendment at all. -->
    <div class="banner info">
      Tournée reprise {round.reopen_count} fois{round.last_reopened_at
        ? `, la dernière à ${new Date(round.last_reopened_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
        : ''}. Le relevé ci-dessous est celui qui fait foi.
    </div>
  {/if}

  {#if reopenable}
    <div class="row wrap" style="margin-bottom:.75rem">
      <button class="grow" onclick={reopen}>Reprendre cette tournée</button>
      <span class="tiny subtle">moins de {REOPEN_WINDOW_HOURS} h</span>
    </div>
  {/if}

  {#if round.mode === 'correction'}
    <div class="banner info">
      Enregistrement hors tournée : ces chariots ont été relevés sans faire le tour du site.
      Ils comptent, et rien d'autre n'a été déclaré manquant.
    </div>
  {:else if round.status === 'abandoned'}
    <div class="banner info">
      Interrompue : les scans comptent, mais aucun chariot n'a été déclaré manquant.
    </div>
  {:else if round.status === 'open'}
    <div class="banner warn">
      <span class="grow">
        {round.reopen_count > 0 ? 'Reprise, pas encore re-clôturée.' : 'Jamais terminée.'}
      </span>
      {#if store.activeRound?.round_id === round.round_id}
        <button class="slim" onclick={() => router.go('')}>Reprendre</button>
      {/if}
    </div>
  {/if}

  <div class="stat-row" class:hidden={round.mode === 'correction'}>
    <div class="stat"><div class="k">attendus</div><div class="n">{progress?.expected.length ?? 0}</div></div>
    <div class="stat is-ok"><div class="k">trouvés</div><div class="n">{progress?.found.length ?? 0}</div></div>
    <div class="stat"><div class="k">d'ailleurs</div><div class="n">{progress?.arrived.length ?? 0}</div></div>
    <div class="stat" class:is-warn={missing.length > 0}>
      <div class="k">manquants</div><div class="n">{missing.length}</div>
    </div>
  </div>

  <div class="card">
    <h2>Scans ({scans.length})</h2>
    {#if scans.length === 0}
      <p class="empty">Aucun chariot scanné pendant cette tournée.</p>
    {:else}
      <ul class="list">
        {#each scans as e (e.id)}
          {@const code = e.type === 'cart_scanned' ? e.cart_code : ''}
          <li class:withdrawn={retracted.has(e.id)}>
            <span class="mono tiny subtle time">{hms(e.at)}</span>
            <button class="row-link" onclick={() => router.go(`carts/${code}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{label(code)}</span>
                <span class="mono tiny muted">{code}</span>
              </span>
            </button>
            {#if retracted.has(e.id)}
              <span class="pill">retiré</span>
            {:else}
              {#if expectedHere.has(code)}
                <span class="pill ok">attendu ici</span>
              {:else if arrivedFrom[code]}
                <span class="pill accent truncate">depuis {store.siteName(arrivedFrom[code])}</span>
              {:else}
                <span class="pill accent">1<sup>er</sup> repérage</span>
              {/if}
              {#if canRetract(e.at)}
                <button
                  class="ghost slim no-print"
                  aria-label="Retirer ce scan"
                  onclick={() => (dropping = { id: e.id, at: e.at, round_id: id, cart_code: code, site_code: e.site_code })}
                >Retirer</button>
              {/if}
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if missing.length > 0}
    {@const declared = round.status === 'closed'}
    <div class="card">
      <h2>{declared ? 'Non trouvés' : 'Pas encore trouvés'} ({missing.length})</h2>
      <p class="tiny muted">
        {#if declared}
          Retrouvé un depuis ? Enregistrez-le seul — la tournée n'est pas à refaire.
        {:else}
          La tournée est ouverte : ces chariots ne sont déclarés manquants qu'à la clôture.
        {/if}
      </p>
      <ul class="list">
        {#each missing as code (code)}
          {@const back = seenSince(code)}
          <li>
            <button class="row-link" onclick={() => router.go(`carts/${code}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{label(code)}</span>
                <span class="mono tiny muted">{code}</span>
              </span>
            </button>
            {#if !declared}
              <span class="pill">à trouver</span>
            {:else if back}
              <span class="pill ok">revu le {new Date(back).toLocaleDateString('fr-FR')}</span>
            {:else}
              <span class="pill danger">manquant</span>
              <button class="slim no-print" onclick={() => (findingCart = code)}>Retrouvé</button>
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  {/if}

  <div class="row" style="margin-top:.2rem">
    <span class="tiny subtle grow">Appareil <span class="mono">{round.device_id.slice(0, 8)}</span></span>
    <button class="ghost slim" onclick={() => router.go(`sites/${round.site_code}`)}>Voir le site →</button>
  </div>
{/if}

{#if dropping}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Retirer ce scan">
    <div class="sheet">
      <h2>Retirer ce scan ?</h2>
      <p class="small muted">
        {label(dropping.cart_code)} ne comptera plus pour cette tournée, et retrouvera la
        position qu'il avait avant. Le scan reste dans l'historique, marqué retiré.
      </p>
      <div class="row">
        <button class="danger grow" onclick={dropScan}>Retirer</button>
        <button class="ghost" onclick={() => (dropping = null)}>Annuler</button>
      </div>
    </div>
  </div>
{/if}

{#if findingCart && round}
  <CorrectionSheet
    site_code={round.site_code}
    cart_code={findingCart}
    onclose={() => (findingCart = null)}
  />
{/if}
<style>
  .time { flex: none; width: 4.6rem; }
  /* A correction has no denominator: nothing was expected, nothing was concluded. */
  .stat-row.hidden { display: none; }
  /* Withdrawn, not erased — the log kept the reading, the count did not. */
  .withdrawn .row-title, .withdrawn .time { text-decoration: line-through; opacity: .55; }
</style>
